import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AppRoutes } from '@/app/routes';
import { RouteFallback } from '@/components/RouteFallback';
import { getPlatform, publicSiteUrl } from '@/lib/platform';

/**
 * Native app only: a website page (legal texts) opens in the in-app browser,
 * and the app goes back to where the link was tapped.
 */
export function NativeExternalPage({ path }: { path: string }) {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  // StrictMode runs effects twice: one browser tab, one step back.
  const opened = useRef(false);

  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    getPlatform()
      .browser.open(publicSiteUrl(path))
      .catch(() => toast.error(t('nativeApp.open_error')));
    // React Router keeps the history index: 0 means the app was opened on this page.
    const historyIndex = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (historyIndex > 0) navigate(-1);
    else navigate(AppRoutes.dashboard.home, { replace: true });
  }, [navigate, path, t]);

  return <RouteFallback />;
}
